import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  buildGridspotCutoutMaskPath,
  buildGridspotOutlinePath,
  findGridspotOption,
  findGridspotOptionById,
  getGridspotCellFromPoint,
  getGridspotCells,
  getGridspotColumns,
  getGridspotConfigurationError,
  getGridspotImageUrl,
  getGridspotRows,
  getGridspotTextOverlays,
} from "../../helpers/gridspot";
import "./GridSpotImage.css";

function buildCutoutMaskImage(cutoutPath) {
  if (!cutoutPath) return undefined;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none"><path fill="white" fill-rule="evenodd" d="${cutoutPath}"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const UNAVAILABLE_MESSAGE = {
  "missing-image": "Image unavailable",
  "invalid-grid": "This question is not set up correctly.",
  "no-options": "This question is not set up correctly.",
  "invalid-options": "This question is not set up correctly.",
  "duplicate-cells": "This question is not set up correctly.",
  "image-load-failed": "Image unavailable",
};

/** Letterboxed draw area for object-fit: contain; null until the image has dimensions. */
function getImageContentAreaMetrics(imageEl) {
  const rect = imageEl.getBoundingClientRect();
  const naturalWidth = imageEl.naturalWidth || 0;
  const naturalHeight = imageEl.naturalHeight || 0;

  if (!naturalWidth || !naturalHeight || !rect.width || !rect.height) {
    return null;
  }

  let contentWidth = rect.width;
  let contentHeight = rect.height;
  let offsetX = 0;
  let offsetY = 0;

  const elementRatio = rect.width / rect.height;
  const imageRatio = naturalWidth / naturalHeight;
  if (elementRatio > imageRatio) {
    contentWidth = rect.height * imageRatio;
    offsetX = (rect.width - contentWidth) / 2;
  } else if (elementRatio < imageRatio) {
    contentHeight = rect.width / imageRatio;
    offsetY = (rect.height - contentHeight) / 2;
  }

  return {
    rect,
    offsetX,
    offsetY,
    contentWidth,
    contentHeight,
  };
}

function getContentAreaStyle(imageEl) {
  const metrics = getImageContentAreaMetrics(imageEl);
  if (!metrics) {
    return null;
  }

  const { rect, offsetX, offsetY, contentWidth, contentHeight } = metrics;
  return {
    left: `${(offsetX / rect.width) * 100}%`,
    top: `${(offsetY / rect.height) * 100}%`,
    width: `${(contentWidth / rect.width) * 100}%`,
    height: `${(contentHeight / rect.height) * 100}%`,
  };
}

function pointerOffsetInImage(event, imageEl) {
  const metrics = getImageContentAreaMetrics(imageEl);
  if (!metrics) {
    return null;
  }

  const point = event.changedTouches?.[0] || event.touches?.[0] || event;
  const { rect, offsetX, offsetY, contentWidth, contentHeight } = metrics;

  return {
    x: point.clientX - rect.left - offsetX,
    y: point.clientY - rect.top - offsetY,
    width: contentWidth,
    height: contentHeight,
  };
}

function GridspotUnavailable({ message }) {
  return (
    <div className="gridspot-image">
      <div className="gridspot-image__missing">{message}</div>
    </div>
  );
}

export default function GridSpotImage({
  question,
  selectedOptionId,
  selectedOptionIds,
  disabled = false,
  readOnly = false,
  onSelect,
  onMiss,
  alt,
  accentColor,
}) {
  const imageRef = useRef(null);
  const activatingRef = useRef(false);
  const rows = getGridspotRows(question);
  const columns = getGridspotColumns(question);
  const imageUrl = getGridspotImageUrl(question);
  const configurationError = readOnly
    ? null
    : getGridspotConfigurationError(question);
  let resolvedSelectedIds = [];
  if (Array.isArray(selectedOptionIds)) {
    resolvedSelectedIds = selectedOptionIds;
  } else if (selectedOptionId != null && selectedOptionId !== "") {
    resolvedSelectedIds = [selectedOptionId];
  }
  const selectedOptions = resolvedSelectedIds
    .map((id) => findGridspotOptionById(question?.options, id))
    .filter(Boolean);
  const [contentAreaStyle, setContentAreaStyle] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [locale, setLocale] = useState(
    () => localStorage.getItem("locale_lang") || "en",
  );

  const syncImageLayout = useCallback(() => {
    const imageEl = imageRef.current;
    if (!imageEl) {
      setContentAreaStyle(null);
      setImageReady(false);
      return;
    }

    const style = getContentAreaStyle(imageEl);
    setContentAreaStyle(style);
    setImageReady(Boolean(style));
  }, []);

  useEffect(() => {
    setImageLoadFailed(false);
    setImageReady(false);
    setContentAreaStyle(null);
    activatingRef.current = false;
  }, [imageUrl, question?._id]);

  useEffect(() => {
    const imageEl = imageRef.current;
    if (!imageEl || configurationError || imageLoadFailed) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      syncImageLayout();
    });
    observer.observe(imageEl);

    return () => observer.disconnect();
  }, [
    configurationError,
    imageLoadFailed,
    imageUrl,
    question?._id,
    syncImageLayout,
  ]);

  const handleImageLoad = useCallback(() => {
    syncImageLayout();
  }, [syncImageLayout]);

  useEffect(() => {
    const syncLocale = () => {
      setLocale(localStorage.getItem("locale_lang") || "en");
    };
    window.addEventListener("storage", syncLocale);
    return () => window.removeEventListener("storage", syncLocale);
  }, []);

  // Union of every cell on every selected option (multi-select highlights all).
  const selectedCells = selectedOptions.flatMap((option) =>
    getGridspotCells(option),
  );
  // Outline the union (one continuous perimeter per connected region).
  const outlinePath = selectedCells.length
    ? buildGridspotOutlinePath(selectedCells, rows, columns)
    : null;
  // Even-odd mask: full frame minus selected cells → cutouts stay sharp.
  const cutoutPath = selectedCells.length
    ? buildGridspotCutoutMaskPath(selectedCells, rows, columns)
    : null;
  const cutoutMaskImage = buildCutoutMaskImage(cutoutPath);
  const textOverlays = getGridspotTextOverlays(question, locale);

  const frameRef = useRef(null);

  const spawnRipple = (event) => {
    const frameEl = frameRef.current;
    if (!frameEl) return;
    const frameRect = frameEl.getBoundingClientRect();
    const point = event.changedTouches?.[0] || event.touches?.[0] || event;
    const ripple = document.createElement("span");
    ripple.className = "gridspot-image__ripple";
    ripple.style.left = `${point.clientX - frameRect.left}px`;
    ripple.style.top = `${point.clientY - frameRect.top}px`;
    frameEl.appendChild(ripple);
    ripple.addEventListener("animationend", () => ripple.remove(), {
      once: true,
    });
  };

  const handleActivate = (event) => {
    if (
      readOnly ||
      disabled ||
      configurationError ||
      imageLoadFailed ||
      !imageReady ||
      activatingRef.current
    ) {
      return;
    }
    const imageEl = imageRef.current;
    if (!imageEl || !rows || !columns) return;

    const pointer = pointerOffsetInImage(event, imageEl);
    if (!pointer) {
      return;
    }

    activatingRef.current = true;

    try {
      const cell = getGridspotCellFromPoint({
        x: pointer.x,
        y: pointer.y,
        width: pointer.width,
        height: pointer.height,
        rows,
        columns,
      });
      if (!cell) {
        spawnRipple(event);
        onMiss?.();
        return;
      }

      const option = findGridspotOption(question?.options, cell, rows, columns);
      if (option) {
        spawnRipple(event);
        // Normalised tap position (0-1 of the image). A gridspot can cover many
        // cells, so the option id alone cannot say where the tap landed. Stored
        // relative to the image rather than as a cell so it stays meaningful if
        // the author later resizes the overlay grid.
        onSelect?.(option, {
          x: pointer.x / pointer.width,
          y: pointer.y / pointer.height,
        });
        return;
      }
      spawnRipple(event);
      onMiss?.();
    } finally {
      // Release after each activation so Client Connect can change selection.
      // Desktop/mobile navigation is guarded by parent selectingRef locks.
      activatingRef.current = false;
    }
  };

  if (configurationError) {
    return (
      <GridspotUnavailable
        message={
          UNAVAILABLE_MESSAGE[configurationError] ||
          UNAVAILABLE_MESSAGE["invalid-grid"]
        }
      />
    );
  }

  if (!imageUrl || !String(imageUrl).trim()) {
    return (
      <GridspotUnavailable message={UNAVAILABLE_MESSAGE["missing-image"]} />
    );
  }

  if (imageLoadFailed) {
    return (
      <GridspotUnavailable message={UNAVAILABLE_MESSAGE["image-load-failed"]} />
    );
  }

  const frameClassName = [
    "gridspot-image__frame",
    readOnly ? "gridspot-image__frame--readonly" : "",
    disabled ? "gridspot-image__frame--disabled" : "",
    !readOnly && !imageReady && !disabled
      ? "gridspot-image__frame--loading"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rootClassName = [
    "gridspot-image",
    readOnly ? "gridspot-image--readonly" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const outlineColor =
    accentColor && String(accentColor).trim()
      ? String(accentColor).trim()
      : "#3aac34";

  const rootStyle = {
    "--gridspot-outline-color": outlineColor,
  };

  return (
    <div className={rootClassName} style={rootStyle}>
      <div
        ref={frameRef}
        className={frameClassName}
        onClick={readOnly ? undefined : handleActivate}
        role="presentation"
      >
        <img
          ref={imageRef}
          className="gridspot-image__img"
          src={imageUrl}
          alt={alt || "Survey image"}
          draggable={false}
          onLoad={handleImageLoad}
          onError={() => setImageLoadFailed(true)}
        />
        {contentAreaStyle &&
        (outlinePath || cutoutMaskImage || textOverlays.length) ? (
          <span
            className="gridspot-image__content-area"
            style={contentAreaStyle}
          >
            {cutoutMaskImage ? (
              <span
                className="gridspot-image__dim"
                style={{
                  WebkitMaskImage: cutoutMaskImage,
                  maskImage: cutoutMaskImage,
                }}
                aria-hidden
              />
            ) : null}
            {outlinePath ? (
              <svg
                className="gridspot-image__outline"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                aria-hidden
                focusable="false"
              >
                {/* White halo keeps the brand stroke readable on busy photos */}
                <path
                  className="gridspot-image__outline-halo"
                  d={outlinePath}
                />
                <path
                  className="gridspot-image__outline-path"
                  d={outlinePath}
                />
              </svg>
            ) : null}
            {textOverlays.map((overlay) => (
              <span
                key={overlay.key}
                className="gridspot-image__label"
                style={overlay.boxStyle}
              >
                <span
                  className="gridspot-image__label-text"
                  style={overlay.labelStyle}
                >
                  {overlay.text}
                </span>
              </span>
            ))}
          </span>
        ) : null}
      </div>
    </div>
  );
}
